/** The step between choosing a file and comparing it: *which column of yours is which of ours* (Gate 38).
 *
 * A first customer's export is not shaped like the template, and until now such a file hit a
 * dead end — a list of the names it was missing. This is the way forward, and it is built around
 * one refusal: **it never decides for the person.**
 *
 * - A proposal from the server is a starting value, shown with why it was made. A header that
 *   could be either of two columns is proposed as neither (`basis: conflict`), so it starts empty.
 * - The comparison button is not offered while a required column has nothing matched to it, or
 *   two columns are claiming the same one — and the sentence says which column, not that
 *   "something is missing".
 * - A column left out is shown as left out (*Don't import*), and the dry run names it again.
 *
 * What this does not do is rename values: `Natural gas` in a category column is still refused row
 * by row by the parser, and a unit that is not in the file cannot be supplied here. Said on the
 * screen rather than left for the person to find out.
 */

import { useState } from "react";

import {
  forgetColumnMapping,
  useSavedColumnMapping,
  type ColumnMap,
  type ColumnProposal,
  type ColumnsNeedMapping,
} from "../api/queries";
import {
  NOT_IMPORTED,
  TARGET_LABEL,
  choiceFromSaved,
  initialChoice,
  problems,
  toColumnMap,
} from "./columnMap";
import type { Choice } from "./columnMap";
import styles from "./Screen.module.css";
import own from "./ColumnMapStep.module.css";

function why(column: ColumnProposal): { text: string; check: boolean } {
  if (column.basis === "name") return { text: "Matched by its name.", check: false };
  if (column.basis === "alias") {
    return { text: "Suggested from its name — check it is the right column.", check: true };
  }
  if (column.basis === "conflict") {
    return {
      text: "Could be the same column as another one of yours, so nothing is suggested. Choose.",
      check: true,
    };
  }
  return { text: "We do not recognise this name, so it is not imported unless you choose.", check: false };
}

/** A file name as a starting name for the saved mapping: no extension, at most the 80 the server keeps. */
function suggestedName(filename: string): string {
  const base = filename.replace(/\.[^.]+$/, "").trim();
  return (base || "Column mapping").slice(0, 80);
}

export function ColumnMapStep({
  needs,
  filename,
  busy,
  refusal,
  onConfirm,
  onCancel,
}: {
  needs: ColumnsNeedMapping;
  filename: string;
  busy: boolean;
  /** The server's sentence if it refused the mapping that was sent — shown, not swallowed. */
  refusal: string | null;
  /** `remember` is set when the person asked to keep the mapping; the screen saves it **after** the
   * comparison has accepted it (ADR-0104), never before. */
  onConfirm: (map: ColumnMap, remember: { name: string } | null) => void;
  onCancel: () => void;
}) {
  const [choice, setChoice] = useState<Choice>(() => initialChoice(needs));
  const blocked = problems(needs, choice);

  //: **ADR-0104.** A mapping saved for exactly these columns is *offered*; it changes nothing until
  //: the person presses the button, and even then only fills the selects they still confirm.
  const saved = useSavedColumnMapping(needs.columns.map((column) => column.header));
  const offered = saved.data ?? null;
  const fill = offered ? choiceFromSaved(needs, offered.mapping) : null;
  const [filledFrom, setFilledFrom] = useState<string | null>(null);
  const [forgotten, setForgotten] = useState(false);
  const [forgetFailed, setForgetFailed] = useState(false);
  const [remember, setRemember] = useState(true);
  const [name, setName] = useState(() => suggestedName(filename));

  return (
    <div className={own.step} data-testid="column-mapping">
      <p className={styles.note}>
        <strong>{filename || "This file"}</strong> is not laid out like our template, so nothing has
        been compared yet. Say which column of yours is which of ours. Nothing is guessed: a column
        you leave as <em>Don&rsquo;t import</em> is left out, and every required column has to be
        matched before the comparison runs.
      </p>

      {offered && fill && !forgotten ? (
        <div className={own.offer} data-testid="saved-mapping-offer">
          <p>
            <strong>You mapped a file with exactly these columns before.</strong> It is saved as{" "}
            <em>{offered.name}</em>. It has not been applied: fill it in to see what it chooses for
            each column, then check it and compare.
          </p>
          <p>
            <button
              type="button"
              className={styles.primary}
              data-testid="use-saved-mapping"
              onClick={() => {
                setChoice(fill);
                setFilledFrom(offered.name);
                setName(offered.name);
              }}
            >
              Fill in from the saved mapping
            </button>{" "}
            <button
              type="button"
              className={styles.fixLink}
              data-testid="forget-saved-mapping"
              onClick={() => {
                setForgetFailed(false);
                void forgetColumnMapping(offered.id).then((gone) => {
                  if (gone) setForgotten(true);
                  else setForgetFailed(true);
                });
              }}
            >
              Forget it
            </button>
          </p>
        </div>
      ) : null}
      {/* **A failed lookup is not "nothing saved".** The step works without the offer, so this does not
          block anything; it only stops the screen from implying that no saved mapping exists. */}
      {saved.error ? (
        <p className={styles.note} role="status" data-testid="saved-mapping-lookup-failed">
          Whether you have saved a mapping for these columns could not be checked, so none is offered.
          You can still map them by hand below.{" "}
          <button type="button" className={styles.fixLink} onClick={() => void saved.refetch()}>
            Try again
          </button>
        </p>
      ) : null}
      {forgetFailed ? (
        <p className={styles.error} role="alert" data-testid="forget-saved-mapping-failed">
          The saved mapping could not be forgotten. It is still saved.
        </p>
      ) : null}
      {filledFrom ? (
        <p className={styles.note} data-testid="saved-mapping-filled">
          Filled in from the saved mapping <em>{filledFrom}</em>. Check every column below before you
          compare; change any that is wrong.
        </p>
      ) : null}

      <ul className={own.columns}>
        {needs.columns.map((column) => {
          const reason = why(column);
          const selected = choice[column.header] ?? NOT_IMPORTED;
          return (
            <li key={column.header} className={own.column}>
              <span className={own.header}>{column.header}</span>
              <span className={own.samples}>
                {column.samples.length > 0
                  ? `e.g. ${column.samples.join(" · ")}`
                  : "no values in the first rows"}
              </span>
              <span className={`${own.why} ${reason.check ? own.whyCheck : ""}`}>{reason.text}</span>
              <select
                className={own.choose}
                aria-label={`Our column for “${column.header}”`}
                value={selected}
                onChange={(event) =>
                  setChoice((was) => ({ ...was, [column.header]: event.target.value }))
                }
              >
                <option value={NOT_IMPORTED}>Don&rsquo;t import</option>
                {needs.targets.map((target) => (
                  <option key={target.name} value={target.name}>
                    {TARGET_LABEL[target.name] ?? target.name} ({target.name})
                    {target.required ? " — required" : ""}
                  </option>
                ))}
              </select>
            </li>
          );
        })}
      </ul>

      {blocked.length > 0 ? (
        <div className={own.blocked} role="alert" data-testid="mapping-blocked">
          <strong>Not ready to compare.</strong>
          <ul>
            {blocked.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {refusal ? (
        <p className={styles.error} role="alert" data-testid="mapping-refused">
          <strong>That mapping could not be used.</strong> {refusal}
        </p>
      ) : null}

      <p className={styles.note}>
        Only the columns are mapped. The values in them are read exactly as they would be in our own
        template: a category, a fuel or a unit we do not recognise, an ambiguous unit such as
        &ldquo;gallons&rdquo;, or a date that is not YYYY-MM-DD is still refused, row by row, and a
        spreadsheet formula nothing has calculated is never worked out.
      </p>

      <p className={own.remember}>
        <label>
          <input
            type="checkbox"
            data-testid="remember-mapping"
            checked={remember}
            onChange={(event) => setRemember(event.target.checked)}
          />{" "}
          Remember this mapping for files with exactly these columns
        </label>
        {remember ? (
          <>
            {" "}
            <label>
              Name{" "}
              <input
                type="text"
                data-testid="remember-name"
                maxLength={80}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
          </>
        ) : null}
      </p>
      {remember ? (
        <p className={styles.note}>
          Only the column names and which of ours each is are kept - never a value from the file. It
          is offered again, not applied: you will always see it and confirm it.
        </p>
      ) : null}

      <p>
        <button
          type="button"
          className={styles.primary}
          data-testid="confirm-mapping"
          disabled={blocked.length > 0 || busy || (remember && name.trim() === "")}
          onClick={() =>
            onConfirm(toColumnMap(needs, choice), remember ? { name: name.trim() } : null)
          }
        >
          {busy ? "Comparing…" : "Compare with this mapping"}
        </button>{" "}
        <button type="button" className={styles.fixLink} data-testid="cancel-mapping" onClick={onCancel}>
          Choose another file
        </button>
      </p>
    </div>
  );
}
